import { i18n } from '@/lib/i18n';
import {
  apiErrorSchema,
  ERROR_CODES,
  type ApiErrorDetail,
  type CustomFieldValidationIssue,
  type ErrorCode,
} from '@erp/shared';
import type { z } from 'zod';
import { redirectToQuestionnaireCustomerMode } from '@/lib/questionnaire-customer-mode';
import { recordFeedbackBreadcrumb } from '@/lib/feedback-diagnostics';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

function localizeWeeklyScheduleError(message: string): string {
  return message.replace(/\bcustomFields\.weeklySchedule\.(?:invalid|required)\b/gu, (key) =>
    i18n.t(key),
  );
}

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly traceId: string | undefined;
  readonly details: ApiErrorDetail[] | undefined;
  /** Kurzüberschrift des Servers; sonst leitet die Oberfläche sie aus dem Status ab. */
  readonly title: string | undefined;
  /** Handlungshinweis für den Benutzer. */
  readonly hint: string | undefined;
  /** Referenz auf den Eintrag in der Fehlerzentrale, z. B. „ERR-8F2B". */
  readonly trackingId: string | undefined;
  /** Feldgenaue Zusatzfeld-Fehler; die Oberfläche markiert damit das jeweilige Eingabefeld. */
  readonly validationErrors: CustomFieldValidationIssue[] | undefined;

  constructor(input: {
    code: ErrorCode;
    message: string;
    status: number;
    traceId?: string;
    details?: ApiErrorDetail[];
    title?: string;
    hint?: string;
    trackingId?: string;
    validationErrors?: CustomFieldValidationIssue[];
  }) {
    super(localizeWeeklyScheduleError(input.message));
    this.name = 'ApiError';
    this.code = input.code;
    this.status = input.status;
    this.traceId = input.traceId;
    this.details = input.details?.map((detail) => ({
      ...detail,
      message: localizeWeeklyScheduleError(detail.message),
    }));
    this.title = input.title;
    this.hint = input.hint;
    this.trackingId = input.trackingId;
    this.validationErrors = input.validationErrors?.map((issue) => ({
      ...issue,
      reason: localizeWeeklyScheduleError(issue.reason),
    }));
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  const fallbackCode =
    response.status === 503 ? ERROR_CODES.SERVICE_UNAVAILABLE : ERROR_CODES.INTERNAL_ERROR;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return new ApiError({
      code: fallbackCode,
      message: response.statusText,
      status: response.status,
    });
  }

  const parsed = apiErrorSchema.safeParse(payload);
  if (!parsed.success) {
    return new ApiError({
      code: fallbackCode,
      message: response.statusText,
      status: response.status,
    });
  }

  const { code, message, traceId, details, title, hint, trackingId } = parsed.data.error;
  const { validationErrors } = parsed.data;
  return new ApiError({
    code,
    message:
      message.startsWith('documentNumber.') ||
      message.startsWith('duplication.') ||
      message.startsWith('mailSettings.') ||
      message.startsWith('actionNeeds.') ||
      message.startsWith('whatsapp.') ||
      message.startsWith('listViews.') ||
      message.startsWith('pdfFieldCheck.') ||
      message.startsWith('batchSelection.') ||
      message.startsWith('stockAdjustment.') ||
      message.startsWith('priceGroups.') ||
      message.startsWith('addressTypes.') ||
      message.startsWith('shipping.') ||
      message.startsWith('documentRouting.') ||
      message.startsWith('conditions.') ||
      message.startsWith('ledger.') ||
      message.startsWith('ledgerAdoption.') ||
      message.startsWith('ledgerClosing.') ||
      message.startsWith('supplierCredits.')
        ? i18n.t(message)
        : message,
    status: response.status,
    traceId,
    ...(details === undefined
      ? {}
      : {
          details: details.map((detail) => ({
            ...detail,
            message:
              detail.message.startsWith('documentNumber.') ||
              detail.message.startsWith('mailSettings.') ||
              detail.message.startsWith('actionNeeds.') ||
              detail.message.startsWith('whatsapp.') ||
              detail.message.startsWith('listViews.') ||
              detail.message.startsWith('pdfFieldCheck.') ||
              detail.message.startsWith('batchSelection.') ||
              detail.message.startsWith('stockAdjustment.') ||
              detail.message.startsWith('priceGroups.') ||
              detail.message.startsWith('addressTypes.') ||
              detail.message.startsWith('shipping.') ||
              detail.message.startsWith('documentRouting.') ||
              detail.message.startsWith('conditions.') ||
              detail.message.startsWith('ledger.') ||
              detail.message.startsWith('ledgerAdoption.') ||
              detail.message.startsWith('ledgerClosing.') ||
              detail.message.startsWith('supplierCredits.')
                ? i18n.t(detail.message)
                : detail.message,
          })),
        }),
    ...(title === undefined ? {} : { title }),
    ...(hint === undefined ? {} : { hint }),
    ...(trackingId === undefined ? {} : { trackingId }),
    ...(validationErrors === undefined ? {} : { validationErrors }),
  });
}

function reactToQuestionnaireCustomerLock(error: ApiError): ApiError {
  if (error.code === ERROR_CODES.QUESTIONNAIRE_CUSTOMER_SESSION_LOCKED) {
    redirectToQuestionnaireCustomerMode();
  }
  return error;
}

const REFRESH_PATH = '/api/v1/auth/refresh';
const UNAUTHENTICATED_EVENT = 'erp:unauthenticated';
const API_MUTATION_EVENT = 'erp:api-mutation';
const MAINTENANCE_EVENT = 'erp:maintenance-mode';

let refreshPromise: Promise<boolean> | null = null;

/** Broadcasts a definitive session loss so the router can redirect to the login route. */
function notifyUnauthenticated(): void {
  window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
}

export function onUnauthenticated(listener: () => void): () => void {
  window.addEventListener(UNAUTHENTICATED_EVENT, listener);
  return () => window.removeEventListener(UNAUTHENTICATED_EVENT, listener);
}

export function onMaintenanceMode(listener: (error: ApiError) => void): () => void {
  const handler = (event: Event): void => listener((event as CustomEvent<ApiError>).detail);
  window.addEventListener(MAINTENANCE_EVENT, handler);
  return () => window.removeEventListener(MAINTENANCE_EVENT, handler);
}

function reactToGlobalState(error: ApiError): ApiError {
  const checked = reactToQuestionnaireCustomerLock(error);
  if (checked.code === ERROR_CODES.MAINTENANCE_MODE) {
    window.dispatchEvent(new CustomEvent(MAINTENANCE_EVENT, { detail: checked }));
  }
  return checked;
}

/** Informiert abgeleitete Übersichten über erfolgreiche Fachmutationen. */
export function onApiMutation(listener: (path: string) => void): () => void {
  const handler = (event: Event): void =>
    listener((event as CustomEvent<{ path: string }>).detail.path);
  window.addEventListener(API_MUTATION_EVENT, handler);
  return () => window.removeEventListener(API_MUTATION_EVENT, handler);
}

function notifyApiMutation(path: string, method: string | undefined): void {
  if ((method ?? 'GET') === 'GET') return;
  window.dispatchEvent(new CustomEvent(API_MUTATION_EVENT, { detail: { path } }));
}

async function send(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(`${BASE_URL}${path}`, { credentials: 'include', ...init });
  } catch {
    recordFeedbackBreadcrumb({
      type: 'API_ERROR',
      actionId: 'api.request.failed',
      actionLabelKey: 'diagnostics.actions.apiFailed',
      entityType: null,
      entityId: null,
      result: 'FAILED',
      errorTrackingId: null,
      feature: path.split('/').filter(Boolean)[2] ?? 'api',
    });
    throw new ApiError({
      code: ERROR_CODES.SERVICE_UNAVAILABLE,
      message: 'Die API ist nicht erreichbar.',
      status: 0,
    });
  }
}

/** Rotates the refresh token at most once per burst of parallel 401 responses. */
async function refreshSession(): Promise<boolean> {
  refreshPromise ??= send(REFRESH_PATH, { method: 'POST' })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

/**
 * Erneuert die Sitzung für Verbraucher außerhalb von `fetch` — etwa den
 * EventSource-Strom, der keine eigenen Wiederholungen mit Header-Kontrolle kennt.
 * Schlägt die Erneuerung fehl, wird der endgültige Sitzungsverlust gemeldet.
 */
export async function ensureSession(): Promise<boolean> {
  const refreshed = await refreshSession();
  if (!refreshed) {
    notifyUnauthenticated();
  }
  return refreshed;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Datei-Uploads; der Browser setzt die Grenzkennung selbst. */
  formData?: FormData;
  /** Verified migration evidence is transferred without multipart wrapping. */
  rawBody?: Blob;
  signal?: AbortSignal;
  skipRefresh?: boolean;
  /** Zusätzliche fachliche Header wie Idempotency-Key; Auth bleibt Cookie-basiert. */
  headers?: Readonly<Record<string, string>>;
}

async function requestWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  const { method = 'GET', body, formData, rawBody, signal, skipRefresh = false, headers } = options;

  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(rawBody !== undefined
        ? { 'Content-Type': 'application/octet-stream' }
        : body === undefined || formData !== undefined
          ? {}
          : { 'Content-Type': 'application/json' }),
      ...headers,
    },
    ...(rawBody !== undefined
      ? { body: rawBody }
      : formData === undefined
        ? body === undefined
          ? {}
          : { body: JSON.stringify(body) }
        : { body: formData }),
    ...(signal === undefined ? {} : { signal }),
  };

  const response = await send(path, init);
  if (response.status !== 401 || skipRefresh) {
    return response;
  }

  const refreshed = await refreshSession();
  if (!refreshed) {
    notifyUnauthenticated();
    return response;
  }

  const retried = await send(path, init);
  if (retried.status === 401) {
    notifyUnauthenticated();
  }
  return retried;
}

export async function apiFetch<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: RequestOptions = {},
): Promise<z.infer<TSchema>> {
  const response = await requestWithRefresh(path, options);

  if (!response.ok) {
    const error = reactToGlobalState(await toApiError(response));
    recordFeedbackBreadcrumb({
      type: 'API_ERROR',
      actionId: 'api.request.failed',
      actionLabelKey: 'diagnostics.actions.apiFailed',
      entityType: null,
      entityId: null,
      result: 'FAILED',
      errorTrackingId: error.trackingId ?? null,
      feature: path.split('/').filter(Boolean)[2] ?? 'api',
    });
    throw error;
  }

  notifyApiMutation(path, options.method);

  return schema.parse(await response.json()) as z.infer<TSchema>;
}

export async function apiSend(path: string, options: RequestOptions): Promise<void> {
  const response = await requestWithRefresh(path, options);

  if (!response.ok) {
    const error = reactToGlobalState(await toApiError(response));
    recordFeedbackBreadcrumb({
      type: 'API_ERROR',
      actionId: 'api.request.failed',
      actionLabelKey: 'diagnostics.actions.apiFailed',
      entityType: null,
      entityId: null,
      result: 'FAILED',
      errorTrackingId: error.trackingId ?? null,
      feature: path.split('/').filter(Boolean)[2] ?? 'api',
    });
    throw error;
  }
  notifyApiMutation(path, options.method);
}

interface UploadOptions {
  method?: 'POST' | 'PUT';
  formData: FormData;
  onProgress?: ((percentage: number) => void) | undefined;
  headers?: Readonly<Record<string, string>>;
  skipRefresh?: boolean;
  signal?: AbortSignal | undefined;
}

function sendUpload(path: string, options: UploadOptions): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(options.method ?? 'POST', `${BASE_URL}${path}`);
    request.withCredentials = true;
    request.setRequestHeader('Accept', 'application/json');
    for (const [name, value] of Object.entries(options.headers ?? {})) {
      request.setRequestHeader(name, value);
    }
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        options.onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    });
    options.signal?.addEventListener('abort', () => request.abort(), { once: true });
    request.addEventListener('abort', () =>
      reject(new DOMException('Upload aborted', 'AbortError')),
    );
    request.addEventListener('load', () => resolve(request));
    request.addEventListener('error', () => {
      recordFeedbackBreadcrumb({
        type: 'API_ERROR',
        actionId: 'api.request.failed',
        actionLabelKey: 'diagnostics.actions.apiFailed',
        entityType: null,
        entityId: null,
        result: 'FAILED',
        errorTrackingId: null,
        feature: path.split('/').filter(Boolean)[2] ?? 'api',
      });
      reject(
        new ApiError({
          code: ERROR_CODES.SERVICE_UNAVAILABLE,
          message: 'Die API ist nicht erreichbar.',
          status: 0,
        }),
      );
    });
    request.send(options.formData);
  });
}

/** Multipart upload with progress and the same session rotation as `apiFetch`. */
export async function apiUpload<TSchema extends z.ZodTypeAny>(
  path: string,
  schema: TSchema,
  options: UploadOptions,
): Promise<z.infer<TSchema>> {
  let request = await sendUpload(path, options);
  if (request.status === 401 && options.skipRefresh !== true && (await refreshSession())) {
    request = await sendUpload(path, options);
  }
  if (request.status === 401 && options.skipRefresh !== true) notifyUnauthenticated();
  if (request.status < 200 || request.status >= 300) {
    const response = new Response(request.responseText, {
      status: request.status,
      statusText: request.statusText,
      headers: { 'content-type': request.getResponseHeader('content-type') ?? 'application/json' },
    });
    const error = reactToGlobalState(await toApiError(response));
    recordFeedbackBreadcrumb({
      type: 'API_ERROR',
      actionId: 'api.request.failed',
      actionLabelKey: 'diagnostics.actions.apiFailed',
      entityType: null,
      entityId: null,
      result: 'FAILED',
      errorTrackingId: error.trackingId ?? null,
      feature: path.split('/').filter(Boolean)[2] ?? 'api',
    });
    throw error;
  }
  let payload: unknown;
  try {
    payload = JSON.parse(request.responseText);
  } catch {
    throw new ApiError({
      code: ERROR_CODES.INTERNAL_ERROR,
      message: 'Die Upload-Antwort ist ungültig.',
      status: request.status,
    });
  }
  return schema.parse(payload);
}

const FILENAME_PATTERN = /filename="?([^";]+)"?/u;

export interface DownloadResult {
  blob: Blob;
  filename: string;
}

export async function apiDownload(
  path: string,
  fallbackFilename: string,
  options: RequestOptions = { method: 'GET' },
): Promise<DownloadResult> {
  const response = await requestWithRefresh(path, options);

  if (!response.ok) {
    throw reactToGlobalState(await toApiError(response));
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = FILENAME_PATTERN.exec(disposition);

  return {
    blob: await response.blob(),
    filename: match?.[1] ?? fallbackFilename,
  };
}

export function saveBlob({ blob, filename }: DownloadResult): void {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}
